import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  Eye,
  MousePointerClick,
  TrendingUp,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  Mail,
  Phone,
  FileText,
  RefreshCw,
  ChevronRight,
  ArrowUpRight,
  BarChart3,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, subtitle, color = "primary" }) => (
  <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground/60">{title}</p>
          <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
          {subtitle && <p className="text-xs text-foreground/50 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DeviceIcon = ({ device }) => {
  if (device === "Mobile") return <Smartphone className="w-4 h-4" />;
  if (device === "Tablet") return <Tablet className="w-4 h-4" />;
  return <Monitor className="w-4 h-4" />;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionEvents, setSessionEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, sessionsRes, leadsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/overview`),
        axios.get(`${BACKEND_URL}/api/analytics/sessions?limit=100`),
        axios.get(`${BACKEND_URL}/api/analytics/leads?limit=100`),
      ]);
      setOverview(overviewRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setLeads(leadsRes.data.leads || []);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const viewSession = async (session) => {
    setSelectedSession(session);
    setDialogOpen(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/analytics/session/${session.session_id}`);
      setSessionEvents(res.data.events || []);
    } catch (err) {
      console.error("Failed to fetch session events:", err);
    }
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-background p-6 sm:p-10">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground" data-testid="analytics-title">
              Visitor Analytics
            </h1>
            <p className="text-sm text-foreground/60 mt-1">
              Real-time tracking for rently.properties
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-analytics-button" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard title="Unique Visitors" value={overview.unique_visitors} icon={Users} color="primary" />
            <StatCard title="Total Page Views" value={overview.total_page_views} icon={Eye} color="accent" />
            <StatCard title="Total Leads" value={overview.total_leads} icon={Mail} color="primary" />
            <StatCard
              title="Form Conversion"
              value={`${overview.conversion_rate}%`}
              icon={TrendingUp}
              subtitle={`${overview.form_submits} of ${overview.form_starts} started`}
              color="accent"
            />
          </div>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Visitor Sessions</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-display">Recent Visitors</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Visitor</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead>Landing Page</TableHead>
                        <TableHead>Pages</TableHead>
                        <TableHead>Referrer</TableHead>
                        <TableHead>Last Seen</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10 text-foreground/50">
                            No visitor sessions yet. Data will appear as users browse the site.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sessions.map((s) => (
                          <TableRow key={s.session_id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewSession(s)}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{s.visitor_id?.substring(0, 12)}...</p>
                                <p className="text-xs text-foreground/50">{s.browser} / {s.os}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <DeviceIcon device={s.device} />
                                <span className="text-sm">{s.device}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-mono">{s.landing_page || "/"}</span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{s.pages_viewed?.length || 0}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-foreground/60 truncate max-w-[150px] block">
                                {s.referrer ? new URL(s.referrer).hostname : "Direct"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-foreground/60">{timeAgo(s.last_seen)}</span>
                            </TableCell>
                            <TableCell>
                              <ChevronRight className="w-4 h-4 text-foreground/30" />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-display">All Leads</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Form Type</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10 text-foreground/50">
                            No leads yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        leads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium">{lead.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${lead.email}`} className="text-primary hover:underline text-sm">
                                {lead.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              {lead.phone ? (
                                <a href={`tel:${lead.phone}`} className="text-primary hover:underline text-sm">
                                  {lead.phone}
                                </a>
                              ) : (
                                <span className="text-foreground/40 text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={lead.form_type === "Free Property Analysis" ? "default" : "secondary"}>
                                {lead.form_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-foreground/60 truncate max-w-[250px] block whitespace-pre-line">
                                {lead.message?.substring(0, 120)}{lead.message?.length > 120 ? "..." : ""}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-foreground/60 whitespace-nowrap">
                              {lead.timestamp ? new Date(lead.timestamp).toLocaleDateString() : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights">
            {overview && (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Top Pages */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Top Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overview.top_pages.length === 0 ? (
                      <p className="text-sm text-foreground/50">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {overview.top_pages.map((p, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm font-mono truncate mr-2">{p.page || "/"}</span>
                            <Badge variant="secondary">{p.views}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Devices */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-primary" />
                      Devices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overview.devices.length === 0 ? (
                      <p className="text-sm text-foreground/50">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {overview.devices.map((d, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <DeviceIcon device={d.device} />
                              <span className="text-sm">{d.device}</span>
                            </div>
                            <Badge variant="secondary">{d.count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Browsers */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <Globe className="w-5 h-5 text-primary" />
                      Browsers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overview.browsers.length === 0 ? (
                      <p className="text-sm text-foreground/50">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {overview.browsers.map((b, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm">{b.browser}</span>
                            <Badge variant="secondary">{b.count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Referrers */}
                <Card className="md:col-span-3">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <ArrowUpRight className="w-5 h-5 text-primary" />
                      Traffic Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overview.referrers.length === 0 ? (
                      <p className="text-sm text-foreground/50">All traffic is direct (no referrer data yet)</p>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {overview.referrers.map((r, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <span className="text-sm truncate mr-2">{r.referrer}</span>
                            <Badge variant="secondary">{r.count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Session Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="font-display">Session Details</DialogTitle>
            </DialogHeader>
            {selectedSession && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-foreground/50">Visitor ID</p>
                    <p className="text-sm font-mono">{selectedSession.visitor_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Device</p>
                    <p className="text-sm">{selectedSession.browser} / {selectedSession.os} / {selectedSession.device}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Screen</p>
                    <p className="text-sm">{selectedSession.screen_width}x{selectedSession.screen_height}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Language</p>
                    <p className="text-sm">{selectedSession.language}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Landing Page</p>
                    <p className="text-sm font-mono">{selectedSession.landing_page || "/"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Referrer</p>
                    <p className="text-sm truncate">{selectedSession.referrer || "Direct"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">First Seen</p>
                    <p className="text-sm">{selectedSession.first_seen ? new Date(selectedSession.first_seen).toLocaleString() : "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/50">Last Seen</p>
                    <p className="text-sm">{selectedSession.last_seen ? new Date(selectedSession.last_seen).toLocaleString() : "—"}</p>
                  </div>
                </div>

                {selectedSession.pages_viewed?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-2">Pages Viewed</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedSession.pages_viewed.map((p, i) => (
                        <Badge key={i} variant="secondary" className="font-mono text-xs">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedSession.utm_source && (
                  <div>
                    <p className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-2">UTM Tags</p>
                    <div className="flex gap-2">
                      {selectedSession.utm_source && <Badge>source: {selectedSession.utm_source}</Badge>}
                      {selectedSession.utm_medium && <Badge variant="secondary">medium: {selectedSession.utm_medium}</Badge>}
                      {selectedSession.utm_campaign && <Badge variant="secondary">campaign: {selectedSession.utm_campaign}</Badge>}
                    </div>
                  </div>
                )}

                <Separator />

                <div>
                  <p className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Event Timeline</p>
                  <ScrollArea className="h-[300px]">
                    {sessionEvents.length === 0 ? (
                      <p className="text-sm text-foreground/50">Loading events...</p>
                    ) : (
                      <div className="space-y-2">
                        {sessionEvents.map((evt, i) => (
                          <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
                            <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{evt.event_type}</Badge>
                                <span className="text-xs text-foreground/40">
                                  {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : ""}
                                </span>
                              </div>
                              <p className="text-sm text-foreground/70 mt-0.5">
                                {evt.page && <span className="font-mono">{evt.page}</span>}
                                {evt.element && <span className="ml-2 text-foreground/50">({evt.element})</span>}
                              </p>
                              {evt.data && Object.keys(evt.data).length > 0 && (
                                <p className="text-xs text-foreground/40 mt-0.5">
                                  {JSON.stringify(evt.data)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Analytics;
